import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { ApiPlatformValidationError, NormalizedFormError } from './api-error.model';

type ErrorLike = {
  status?: number;
  message?: string;
  error?: ApiPlatformValidationError & { message?: string };
  violations?: ApiPlatformValidationError['violations'];
  detail?: string;
};

@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  isValidationError422(err: unknown): boolean {
    const status = (err as ErrorLike)?.status ?? (err as ErrorLike)?.error?.status;
    return status === 422;
  }

  extractViolations(err: unknown): NormalizedFormError[] {
    const errorLike = err as ErrorLike;
    const payload = errorLike?.error ?? errorLike;
    const violations = payload?.violations ?? errorLike?.violations;

    if (Array.isArray(violations) && violations.length > 0) {
      return violations
        .filter((violation) => violation?.propertyPath && violation?.message)
        .map((violation) => ({
          path: violation.propertyPath,
          message: violation.message
        }));
    }

    const fallbackMessage = payload?.detail || payload?.message || errorLike?.message || 'Error de validación';
    return fallbackMessage ? [{ path: '', message: fallbackMessage }] : [];
  }

  applyViolationsToForm(form: FormGroup | FormArray, errors: NormalizedFormError[]): void {
    errors.forEach((error) => {
      const normalizedPath = this.normalizePath(error.path);
      const control = normalizedPath ? form.get(normalizedPath) : null;

      if (control) {
        const currentErrors = control.errors || {};
        control.setErrors({ ...currentErrors, backend: error.message });
        control.markAsTouched();
      } else {
        const currentErrors = form.errors || {};
        form.setErrors({ ...currentErrors, backend: error.message });
      }
    });
  }

  buildUserSummary(
    errors: NormalizedFormError[],
    options?: { resolveItemLabel?: (index: number) => string }
  ): string[] {
    const summaries: string[] = [];
    const itemRegex = /^items\[(\d+)\](?:\.(.+))?$/;

    errors.forEach((error) => {
      const match = itemRegex.exec(error.path);
      if (match) {
        const index = Number(match[1]);
        const field = match[2] ?? '';
        const fieldLabel = this.humanizeField(field);
        const itemLabel = options?.resolveItemLabel
          ? options.resolveItemLabel(index)
          : `Ítem #${index + 1}`;
        const suffix = fieldLabel ? `${fieldLabel}: ` : '';
        summaries.push(`${itemLabel}: ${suffix}${error.message}`);
        return;
      }

      if (!error.path) {
        summaries.push(error.message);
        return;
      }

      const fieldLabel = this.humanizeField(error.path);
      summaries.push(`${fieldLabel}: ${error.message}`);
    });

    return summaries;
  }

  focusFirstInvalid(containerEl?: HTMLElement): void {
    const root: ParentNode = containerEl ?? document;
    const invalidControl = root.querySelector<HTMLElement>(
      'input.ng-invalid, textarea.ng-invalid, select.ng-invalid, .ng-invalid input, .ng-invalid textarea, .ng-invalid select'
    );

    if (!invalidControl) {
      return;
    }

    invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    invalidControl.focus();
  }

  private normalizePath(path: string): string {
    if (!path) {
      return '';
    }
    return path.replace(/\[(\d+)\]/g, '.$1');
  }

  private humanizeField(path: string): string {
    if (!path) {
      return 'Campo';
    }

    const lastSegment = path.split('.').pop() || path;
    const cleaned = lastSegment.replace(/\[(\d+)\]/g, '');
    const labels: Record<string, string> = {
      programa: 'Programa',
      diaTipo: 'Día',
      horaDesde: 'Hora desde',
      horaHasta: 'Hora hasta',
      duracionSegundos: 'Duración',
      material: 'Material',
      avisos: 'Avisos',
      ubicacionDigital: 'Ubicación',
      formatoDigital: 'Formato',
      fechaDesde: 'Fecha desde',
      fechaHasta: 'Fecha hasta',
      plataformaRedes: 'Plataforma',
      cantidad: 'Cantidad',
      tituloContenido: 'Título',
      eventoNombre: 'Evento',
      fechaEvento: 'Fecha',
      clientePrincipal: 'Cliente principal',
      anunciante: 'Anunciante',
      productor: 'Productor',
      frecuencia: 'Frecuencia',
      tipoOrden: 'Tipo de orden',
      estado: 'Estado',
      moneda: 'Moneda',
      porcentajeComision: 'Porcentaje de comisión',
      numero: 'Número'
    };

    return labels[cleaned] ?? cleaned;
  }
}
